import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/** Gap between consecutive cards in the opening cascade. */
const STAGGER_STEP_S = 0.06;

/**
 * How many cards take part in the cascade.
 *
 * Only the first screenful should be staggered. Further down the list a card is revealed
 * because the player scrolled to it, and a delay there reads as the app being slow rather
 * than as choreography — so everything past this cut reveals immediately on entry.
 */
const STAGGERED_CARDS = 4;

const EASE_OUT = [0.25, 0.46, 0.45, 0.94] as const;

interface RevealOnScrollProps {
  /** Position in the list, used to work out this card's place in the opening cascade. */
  index?: number;
  className?: string;
  children: React.ReactNode;
}

/**
 * Fades and lifts its child into place the first time it scrolls into view.
 *
 * Reveals `once` — scrolling back up must not replay the entrance, which would turn a
 * long list of picks into a flicker.
 *
 * Renders the child plainly, with no animation at all, whenever the reveal cannot run:
 * under `prefers-reduced-motion`, or with no IntersectionObserver to drive it. The second
 * case is not only the jsdom test runner — without the guard, any host missing the API
 * would leave every card parked at `opacity: 0`, which is to say the picks would never
 * appear at all.
 */
export const RevealOnScroll: React.FC<RevealOnScrollProps> = ({
  index = 0,
  className,
  children,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const canReveal = typeof IntersectionObserver !== 'undefined' && !prefersReducedMotion;

  if (!canReveal) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15, margin: '0px 0px -40px 0px' }}
      transition={{
        duration: 0.35,
        ease: EASE_OUT,
        delay: index < STAGGERED_CARDS ? index * STAGGER_STEP_S : 0,
      }}
    >
      {children}
    </motion.div>
  );
};

export default RevealOnScroll;
